import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { cmApi, unwrapCmDataRecord } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  parseCurrencyType,
  paiseToDisplay,
  type CurrencyType,
} from '@/features/hierarchy/hierarchy'
import type {
  CreditLimitTransferResult,
  CreditLimitTransferRow,
} from '@/features/logs/clTransfersTypes'

/** CM-API `POST /v1/credit-limit-logs/searchCreditLimitLogs` (+ BQ twin) request. */
export type SearchCreditLimitLogsRequest = {
  page?: number
  pageSize?: number
  fromDate?: string
  toDate?: string
}

/** One CM-API CL log row (minor-unit money strings). */
export type CreditLimitLogApiRow = {
  id: string
  createdAt: string
  status?: string
  fromUsername?: string | null
  toUsername?: string | null
  creditLimit?: string | number | null
  balance?: string | number | null
  oldBalance?: string | number | null
  difference?: string
  clientIp?: string | null
}

/** Inner page payload under Standard `data`. */
export type CreditLimitLogsPage = {
  results?: CreditLimitLogApiRow[] | null
  totalCount?: number
  page?: number
  pageSize?: number
}

/** Standard API Response from CM-API (`strix-api-json-io`). */
export type SearchCreditLimitLogsResponse = {
  status: number
  message: string
  data: CreditLimitLogsPage
}

/** Same gate as Invalid Bets — Standard success with a data payload. */
export function isCreditLimitLogsOk(
  live: SearchCreditLimitLogsResponse | null | undefined,
): live is SearchCreditLimitLogsResponse {
  return live?.status === 200 && live.data != null
}

/**
 * Lab `CreditLimitLogs/searchCreditLimitLogs` → CM-API (Postgres).
 * Returns the Standard envelope; map with `toCreditLimitTransferResult`.
 */
function searchBody(body: SearchCreditLimitLogsRequest): SearchCreditLimitLogsRequest {
  const out: SearchCreditLimitLogsRequest = {
    page: body.page ?? 1,
    pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  if (body.fromDate) out.fromDate = body.fromDate
  if (body.toDate) out.toDate = body.toDate
  return out
}

export async function searchCreditLimitLogs(
  body: SearchCreditLimitLogsRequest = {},
): Promise<SearchCreditLimitLogsResponse> {
  return cmApi.searchCreditLimitLogs(authHeaders(), searchBody(body))
}

/**
 * CL Transfers historical range — BigQuery; same request/response as `searchCreditLimitLogs`.
 */
export async function searchCreditLimitLogsBQ(
  body: SearchCreditLimitLogsRequest = {},
): Promise<SearchCreditLimitLogsResponse> {
  return cmApi.searchCreditLimitLogsBQ(authHeaders(), searchBody(body))
}

/**
 * Map CM-API Standard (or already-unwrapped page) → console display units.
 * Uses shared `unwrapCmDataRecord` — no Lab `{ value }` gate.
 */
export function toCreditLimitTransferResult(
  live: SearchCreditLimitLogsResponse | CreditLimitLogsPage | unknown,
  currencyRaw?: string | null,
): CreditLimitTransferResult {
  const ccy: CurrencyType = parseCurrencyType(currencyRaw)
  const toDisplay = (minor: string | number | null | undefined) =>
    paiseToDisplay(minor == null ? null : String(minor), ccy)

  const page = unwrapCmDataRecord(live)
  const rawResults = page.results
  const results = Array.isArray(rawResults) ? (rawResults as CreditLimitLogApiRow[]) : []

  const pageNum = typeof page.page === 'number' && Number.isFinite(page.page) ? page.page : 1
  const pageSize =
    typeof page.pageSize === 'number' && Number.isFinite(page.pageSize)
      ? page.pageSize
      : DEFAULT_PAGE_SIZE
  const total =
    typeof page.totalCount === 'number' && Number.isFinite(page.totalCount)
      ? page.totalCount
      : 0

  const rows: CreditLimitTransferRow[] = results.map((row) => ({
    id: String(row.id ?? ''),
    createdAt: row.createdAt ?? '',
    fromUsername: row.fromUsername ?? '',
    toUsername: row.toUsername ?? '',
    creditLimit: toDisplay(row.creditLimit),
    balance: toDisplay(row.balance),
    oldBalance: toDisplay(row.oldBalance),
    clientIp: row.clientIp ?? '',
  }))
  return { rows, total, page: pageNum, pageSize }
}
